import React, { useState, useRef, useEffect } from 'react';
import {createPortal} from 'react-dom';
import { Elements, useElements, useStripe, PaymentElement } from '@stripe/react-stripe-js';
import useBSSLogger from '../hooks/useBSSLogger';
import { confirmPayment, getPaymentIntent } from '../api/stripe';
import useSWRImmutable from 'swr/immutable'
import DefaultLoadingComponent from './form/DefaultLoadingComponent';
import DefaultErrorComponent from './form/DefaultErrorComponent';
import { useTheme } from '@emotion/react';
import { Button } from '@mui/material';
import __t from '../lib/traduzioni';
import { useLocalStorage } from 'react-use';
import { useContext } from 'react';
import BSSPreventivoContext from '../contexts/BSSPreventivoContext';
import Wrapper, {  } from './Wrapper';
import Whitespace from './Whitespace';
import { Typography } from '@mui/material';
import { execute, preExecRequestStripe } from '../lib/pagautils';
import { CreditCard } from '@mui/icons-material';

const {logger} = useBSSLogger();

/**
 * Si occupa di fare le chiamate api per generare il payment intent
 * @param {*} param0 
 * @returns 
 */
const LoadPaymentIntent = ({children}) => {
    const initialData = async () => {
        const resp = await getPaymentIntent();
        return resp;
    }

    const {data: paymentIntent, error} = useSWRImmutable(`/api/stripepaymentintent`, initialData, {
        onErrorRetry: (error, key, config, revalidate) => {
            return             
        }
    })

    if(!error && !paymentIntent){
        return (<DefaultLoadingComponent />) 
    } 

    if(error){
        logger.debug("errore stripe", error)
        return <DefaultErrorComponent {...{error}} />
    } 

    const clientSecret = paymentIntent.stripeclSecret

    return children({clientSecret, dettagliPagamento: paymentIntent})
}

/**
 * Il componente Entrypoint da usare da shortcode è questo
 * Sfrutta l'hoc LoadPaymentIntent, quando è disponibile il payment intent si occupa tramite Portal di mostrare il form di Stripe fuori dallo shadowDOM
 * @returns 
 */
const PayWithStripe = ({stripePromise, methods, modalita, parametri}) => {

    const nodeRef = useRef();  
    
    /**@type {import('@mui/material').Theme} */
    const theme = useTheme();

    logger.debug("tema utilizzato", theme)

    // const jss = create({
    //     ...jssPreset(), 
    //     //insertionPoint: headRef,
    //     plugins: [
    //         ...jssPreset().plugins, 
    //         jssIncreaseSpecificity({ repeat: 1 }), 
    //     ]
    // })

    const [stripeCfg, setStripeCfg] = useState(null)

    //necessario a fare il render dei componenti di stripe fuori da shadowDOM
    useEffect(()=> {

        const closestShadowBody = nodeRef?.current?.offsetParent ?? null;
        logger.debug("ottengo l'elemento di shadowdom per PayWithStripe", closestShadowBody)
        if(closestShadowBody){
            const id = (closestShadowBody?.id ?? "").split("_")?.reverse()?.[0];
            const wrapper = document.querySelector(`section#bss_shortcode_wrapper_${id}`);
            let lightDOMSection = document.createElement("section");
            lightDOMSection.id = `bss_shortcode_wrapper_lightDOM_stripe`;
            wrapper.appendChild(lightDOMSection)            
            setStripeCfg({dest: lightDOMSection})
        }
        
    }, [nodeRef])
    
    const {dest: stripeDest = null} = (stripeCfg ?? {});        

    

    return  (
        <section ref={nodeRef}>
            <LoadPaymentIntent>
                {({clientSecret, dettagliPagamento}) => {
                    /**
                     * @type {import('@stripe/stripe-js').StripeElementsOptions}
                     */
                    const stripeElementsOptions = {
                        clientSecret,                         
                        appearance: {
                            theme: theme.palette.mode === 'dark' ? 'night' : "stripe",     
                            variables: {
                                colorDanger: theme.palette.error.main,
                                colorDangerText: theme.palette.error.main,
                                borderRadius: theme.spacing(1/2)
                            },     
                            rules: {
                                ".Error": {
                                    color: theme.palette.error.main,
                                    borderColor: theme.palette.error.main
                                }
                            },
                            labels: "above"     
                        }
                    }                    

                    return stripeDest && clientSecret && (
                        <>
                            {createPortal(                                                   
                                <section style={{
                                    maxWidth: `${theme.breakpoints.values.xl}${theme.breakpoints.unit}`, 
                                    margin: "auto",
                                }}>
                                    <Elements stripe={stripePromise} options={{...stripeElementsOptions}}>                            
                                        <StripeForm 
                                            clientSecret={clientSecret} 
                                            dettagliPagamento={dettagliPagamento} 
                                            methods={methods} 
                                            modalita={modalita}
                                            parametri={parametri}
                                        />
                                    </Elements>                                
                                </section>                                                                                              
                                , stripeDest
                            )}
                        </>
                        
                    )        
                    
                }}
            </LoadPaymentIntent>
        </section>
        
    );
};

/**
 * 
 * @param {{clientSecret: String, methods: import('react-hook-form').UseFormReturn}} param0 
 * @returns 
 */
const StripeForm = ({clientSecret, dettagliPagamento, methods, modalita, parametri}) => {

    const contesto = useContext(BSSPreventivoContext);
    const [carrelloStorage = {preventivi: [], pagamenti: {}, datiFatturazione: {}}, aggiornaCarrelloStorage, rimuoviCarrelloStorage] = useLocalStorage("spedizioniweb2_preventivo_carrello");
    const {preventivoStorage = {}, rimuoviPreventivoStorage, datiFatturazione, tipiPersone, backdropPreventivo, setBackdropPreventivo} = contesto;
    
    const elements = useElements();
    const stripe = useStripe();
    const theme = useTheme();    

    const handleSubmit = async (event) => {

        execute({
            event, 
            modalita, 
            methods,             
            execPayment: confirmPayment, 
            carrelloStorage, 
            rimuoviCarrelloStorage, 
            rimuoviPreventivoStorage, 
            specificPreExecRequest: preExecRequestStripe,
            preExecRequestParams: {stripe, elements},
            backdropPreventivo, 
            setBackdropPreventivo
        })        
                
    }

    //const {importotransazione = null} = dettagliPagamento
    const {totalecarrello, totaleConCommissione, prezzoCommissione} = modalita;

    return(
        <>
            <section style={{
                paddingTop: theme.spacing(2),
                paddingLeft: theme.spacing(6),                
                paddingRight: theme.spacing(6),         
            }}>
                <PaymentElement />            
            </section>

            {/* <CardElement /> */}
            <Wrapper inPortal paperProps={{sx: {boxShadow: "none", px: 2}}}> 
                {/* <pre>{JSON.stringify(theme, null, 2)}</pre> */}
                
                <Button sx={{width: "100%"}} variant="contained" onClick={handleSubmit} disabled={!stripe}>
                    <Typography variant='h6'>
                        <CreditCard />
                        <Whitespace />
                        {__t("pagaconstripe")} 
                        
                        <>
                            <Whitespace />
                            <b>
                                {`€ ${totaleConCommissione ?? totalecarrello}`}
                            </b>
                        </>
                    
                    </Typography>                        
                </Button>  
                
                
            </Wrapper>
            
        </>
    )
}

export default PayWithStripe;

